import { apiClient, getApiBaseUrl } from '../core/api/apiClient';
import type { PromptApi, PromptResponse } from './dashboard.service';

export interface ProposalMilestone {
  period: string;
  text: string;
}

/** Project Highlights. The Budget bullet is not stored: the API sends it as `budget_line`. */
export interface ProposalHighlights {
  problem: string;
  solution: string;
  impact: string;
  milestones: ProposalMilestone[];
}

export interface ReplacedDocument {
  key: string;
  replaced_at: string;
  replaced_by?: string;
  replaced_with: string[];
  /** Set when "keep old file" put the replaced file back: this upload was set aside. */
  not_used?: boolean;
}

export interface Proposal {
  _id: string;
  proposal_id?: string | null;
  slug?: string;
  title: string;
  domain: string;
  sub_domain?: string | null;
  pi?: string | null;
  co_pis?: string[];
  /** Original Faculty/Centre text from the Excel import. */
  faculty?: string | null;
  categorization?: string | null;
  budget_lakhs: number | null;
  budget_note?: string | null;
  duration_months?: number | null;
  project_type?: string | null;
  department?: string | null;
  keywords?: string[];
  un_sdg?: string | null;
  proposal_period?: string | null;
  document_nature?: string | null;
  posters_available?: string | null;
  /** Shown as "Summary". */
  description?: string | null;
  highlights?: ProposalHighlights | null;
  /** Generated by the API from budget and duration, e.g. "The total required budget is 25 lakhs (…)". */
  budget_line?: string;
  documents?: string[];
  replaced_documents?: ReplacedDocument[];
  /** S.No from the imported sheet (e.g. "25A"). */
  source_sn?: string | null;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  imported_at?: string;
}

/** Create / edit body. On edit, only changed fields need to be sent. */
export type ProposalInput = Partial<Pick<Proposal,
  'title' | 'domain' | 'sub_domain' | 'pi' | 'co_pis' | 'department' | 'budget_lakhs' | 'duration_months' |
  'description' | 'un_sdg' | 'keywords' | 'highlights' | 'categorization' | 'proposal_period' | 'project_type' |
  'document_nature' | 'posters_available'>>;

/** Create body. After "Analyse with AI": the fields as the AI filled them, and the text it read,
 * so the AI can learn from what the reviewer changed. */
export type ProposalCreateInput = ProposalInput & { ai_proposed?: ProposalInput; document_text?: string };

export interface ProposalOptions {
  domains: string[];
  sub_domains: string[];
  sub_domains_by_domain: Record<string, string[]>;
  periods: string[];
  categorizations: string[];
  project_types: string[];
  departments: string[];
  doc_natures: string[];
  posters: string[];
  sdgs: string[];
  default_period: string;
  required: string[];
  /** Every PI / Co-PI name already stored, most used first (for autocomplete). */
  people: string[];
}

export interface ProposalSuggestion {
  title: string;
  domain: string;
  domain_is_new: boolean;
  sub_domain: string;
  sub_domain_is_new: boolean;
  pi: string;
  co_pis: string[];
  department: string;
  budget_lakhs: number | null;
  duration_months: number | null;
  description: string;
  un_sdg: string;
  keywords: string[];
  highlights: ProposalHighlights;
}

export interface ProposalExtractResponse {
  suggested: ProposalSuggestion;
  document_nature: string | null;
  /** What the AI read (first 12,000 characters); sent back on create for learning. */
  document_text: string;
  error: string | null;
}

export type ProposalChangeValue = string | number | string[] | ProposalMilestone[] | null;

/** One compare-window row: a field the AI would change. `field` is a Proposal key or "highlights.<part>". */
export interface ProposalChange {
  field: string;
  label: string;
  current: ProposalChangeValue;
  suggested: ProposalChangeValue;
  is_new: boolean;
}

export type DocumentUpdateMode = 'add' | 'replace';

export interface ProposalAnalyseResponse extends ProposalExtractResponse {
  changes: ProposalChange[];
}

export interface ProposalUploadResponse {
  uploaded: string[];
  errors: string[];
  replaced: string | null;
  proposal: Proposal;
}

export interface ProposalSaveResponse {
  proposal: Proposal;
  index_pending: boolean;
}

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export interface ProposalsListResponse {
  proposals: Proposal[];
  pages: number;
  total: number;
}

export interface HistorySession {
  id: string;
  title: string;
  current_step: string;
  created_at: string | null;
}

export interface HistorySessionResponse {
  success: boolean;
  sessions?: HistorySession[];
  person_data?: any;
  web_research_data?: any;
  conversation_state?: {
    domain?: string;
    focus?: string;
    count?: number;
  };
  form_data?: {
    budget_text?: string;
  };
  donor_insight?: string;
  proposals?: any[];
}

export const proposalService = {
  listProposals: async (options: { page?: number; limit?: number; q?: string } = {}): Promise<ProposalsListResponse> => {
    return apiClient<ProposalsListResponse>('/proposals', { method: 'GET', params: options });
  },

  getOptions: async (): Promise<ProposalOptions> => {
    return apiClient<ProposalOptions>('/proposals/options', { method: 'GET' });
  },

  extract: async (files: File[]): Promise<ProposalExtractResponse> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return apiClient<ProposalExtractResponse>('/proposals/extract', { method: 'POST', body: formData });
  },

  create: async (body: ProposalCreateInput): Promise<ProposalSaveResponse> => {
    return apiClient<ProposalSaveResponse>('/proposals', { method: 'POST', body: JSON.stringify(body) });
  },

  update: async (pid: string, body: ProposalInput): Promise<ProposalSaveResponse> => {
    return apiClient<ProposalSaveResponse>(`/proposals/${pid}`, { method: 'PUT', body: JSON.stringify(body) });
  },

  remove: async (pid: string): Promise<{ success: boolean; index_pending: boolean }> => {
    return apiClient<{ success: boolean; index_pending: boolean }>(`/proposals/${pid}`, { method: 'DELETE' });
  },

  /** Adds documents; with `replace` (a key on this proposal) swaps that one out. The old file stays in storage. */
  uploadDocuments: async (pid: string, files: File[], replace?: string): Promise<ProposalUploadResponse> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (replace) formData.append('replace', replace);
    return apiClient<ProposalUploadResponse>(`/proposals/${pid}/documents`, {
      method: 'POST',
      body: formData,
    });
  },

  /** Undo a replace: `key` (the replaced file) becomes the main document again; the new one is set aside. */
  restoreDocument: async (pid: string, key: string): Promise<{ proposal: Proposal }> => {
    const formData = new FormData();
    formData.append('key', key);
    return apiClient<{ proposal: Proposal }>(`/proposals/${pid}/documents/restore`, {
      method: 'POST',
      body: formData,
    });
  },

  /** AI pass over a document just added or replacing one; returns the fields it would change. Saves nothing. */
  analyse: async (pid: string, files: File[], mode: DocumentUpdateMode): Promise<ProposalAnalyseResponse> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('mode', mode);
    return apiClient<ProposalAnalyseResponse>(`/proposals/${pid}/analyse`, { method: 'POST', body: formData });
  },

  downloadDocument: async (pid: string, key: string): Promise<void> => {
    const blob = await apiClient<Blob>(`/proposals/${pid}/documents/download`, {
      method: 'GET',
      params: { key },
      responseType: 'blob',
    });
    saveBlob(blob, key.split('/').pop() || 'document');
  },

  exportXlsx: async (q?: string): Promise<void> => {
    const blob = await apiClient<Blob>('/proposals/export', {
      method: 'GET',
      params: q ? { q } : undefined,
      responseType: 'blob',
    });
    saveBlob(blob, `proposals_${new Date().toISOString().slice(0, 10)}.xlsx`);
  },

  getProposalsList: async (options: {
    page?: number;
    limit?: number;
    q?: string;
  } = {}): Promise<ProposalsListResponse> => {
    return apiClient<ProposalsListResponse>('/dashboard/proposals', {
      method: 'GET',
      params: options,
    });
  },

  getHistory: async (): Promise<{ success: boolean; sessions: HistorySession[] }> => {
    return apiClient<{ success: boolean; sessions: HistorySession[] }>('/ai-proposal/history', {
      method: 'GET',
    });
  },

  getSessionDetails: async (sessionId: string): Promise<HistorySessionResponse> => {
    return apiClient<HistorySessionResponse>(`/ai-proposal/history/${sessionId}`, {
      method: 'GET',
    });
  },

  deleteSession: async (sessionId: string): Promise<{ success: boolean }> => {
    return apiClient<{ success: boolean }>(`/ai-proposal/history/${sessionId}`, {
      method: 'DELETE',
    });
  },

  saveProgress: async (payload: {
    session_id: string | null;
    current_step: string;
    person_data: any;
    web_research_data: any;
    form_data: {
      domain: string;
      budget_text: string;
      focus: string;
      count: number;
    };
  }): Promise<{ success: boolean; session_id: string }> => {
    return apiClient<{ success: boolean; session_id: string }>('/ai-proposal/save-progress', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  autofillConfig: async (payload: {
    structured_data: any;
    person: {
      name: string;
      company?: string;
      designation?: string;
      degree?: string;
      department?: string;
      batch?: string;
    };
  }): Promise<{
    success: boolean;
    domain?: string;
    budget?: string;
    interest_focus?: string;
    donor_insight?: string;
  }> => {
    return apiClient<{
      success: boolean;
      domain?: string;
      budget?: string;
      interest_focus?: string;
      donor_insight?: string;
    }>('/ai-proposal/autofill-config', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  searchProposals: async (payload: {
    domain: string;
    budget_text: string;
    focus: string;
    count: number;
    session_id: string | null;
    person_data: any;
    web_research_data: any;
    donor_insight: string;
  }): Promise<Response> => {
    // For stream reading, we hit apiClient but since apiClient resolves json/text,
    // we bypass it or handle raw fetch here with authorization.
    const token = localStorage.getItem('token');
    const API_BASE_URL = getApiBaseUrl();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return fetch(`${API_BASE_URL}/ai-proposal/form-search`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include',
    });
  },
};

/** Admin only — the proposal AI's prompt and the rules it learned from corrections. */
export const proposalPromptApi: PromptApi = {
  getPrompt: async () => apiClient<PromptResponse>('/proposals/admin/prompt', { method: 'GET' }),
  savePrompt: async (template) =>
    apiClient<PromptResponse>('/proposals/admin/prompt', { method: 'PUT', body: JSON.stringify({ template }) }),
  restorePrompt: async () => apiClient<PromptResponse>('/proposals/admin/prompt/restore', { method: 'POST' }),
  patchRule: async (ruleId, payload) =>
    apiClient<PromptResponse>(`/proposals/admin/prompt/rules/${ruleId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  recheckRule: async (ruleId) =>
    apiClient<PromptResponse>(`/proposals/admin/prompt/rules/${ruleId}/recheck`, { method: 'POST' }),
  deleteRule: async (ruleId) =>
    apiClient<PromptResponse>(`/proposals/admin/prompt/rules/${ruleId}`, { method: 'DELETE' }),
};
